import React, { useState, useCallback } from 'react';
import { CtyButton, useHasApplicationResource } from "@circuitry-ai/doc-ui";
import { Icon } from '@iconify/react';
import { useGetFgaGroupsQuery } from "../../../../shared/data/src/features/settings/settingsApiSlice";
import { GenericCellRenderer, ActionButton, AgGridWrapper } from '../components/CellRenderers';
import UserManagementModal from '../components/UserManagementModal';
import GroupModal from '../components/GroupModal';
import DeleteModal from '../components/DeleteModal';

// Extracted components to avoid inline definitions
const GroupNameCellRenderer = (params: any) => <GenericCellRenderer type="groups" data={params.data} field="group" />;

const ResourcePermissionsCellRenderer = (params: any) => {
  const applicationResources = params.data?.application_resources || [];
  const entityTypes = params.data?.entity_types || [];
  const entityResources = params.data?.entity_resources || [];
  
  const appResourceCount = applicationResources.length;
  const entityTypeCount = entityTypes.length;
  const entityResourceCount = entityResources.length;
  const totalCount = appResourceCount + entityTypeCount + entityResourceCount;
  
  if (totalCount === 0) {
    return (
      <div className="flex items-center h-full">
        <span className="text-sm text-default-500">No resources assigned</span>
      </div>
    );
  }
  
  const counts = [];
  if (appResourceCount > 0) {
    const plural = appResourceCount === 1 ? '' : 's';
    counts.push({ text: `${appResourceCount} Application Resource${plural}`, id: 'app-resource' });
  }
  if (entityTypeCount > 0) {
    const plural = entityTypeCount === 1 ? '' : 's';
    counts.push({ text: `${entityTypeCount} Entity Type${plural}`, id: 'entity-type' });
  }
  if (entityResourceCount > 0) {
    const plural = entityResourceCount === 1 ? '' : 's';
    counts.push({ text: `${entityResourceCount} Entity Resource${plural}`, id: 'entity-resource' });
  }
  
  return (
    <div className="flex flex-wrap gap-1 items-center h-full">
      {counts.map((count) => (
        <span key={count.id} className="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-default-100 text-default-700">
          {count.text}
        </span>
      ))}
    </div>
  );
};

const UsersCountCellRenderer = ({ params, onUserManagementClick }: { 
  params: any; 
  onUserManagementClick: (groupName: string) => void;
}) => {
  const userCount = params.data?.member_count || 0;
  const isZero = userCount === 0;
  
  const iconName = isZero ? "mdi:account-plus" : "mdi:account";
  const textColor = isZero ? 'text-default-500' : 'font-medium text-foreground';
  const userText = userCount === 1 ? 'user' : 'users';
  const displayText = isZero ? 'Add users' : `${userCount} ${userText}`;
  
  return (
    <button 
      className="flex items-center h-full gap-2 cursor-pointer hover:bg-default-100 p-2 rounded w-full text-left"
      onClick={() => onUserManagementClick(params.data.group)}
      aria-label={`Manage users for ${params.data.group} group`}
    >
      <Icon icon={iconName} width={16} height={16} className="text-default-500" />
      <span className={`text-sm ${textColor}`}>
        {displayText}
      </span>
    </button>
  );
};

const GroupActionCellRenderer = ({ params, handleDelete, handleEdit, loadingStates, isDisabled }: { 
  params: any; 
  handleDelete: (data: any) => void; 
  handleEdit: (data: any) => void;
  loadingStates: { deletingGroups: Set<string>; }; 
  isDisabled?: boolean;
}) => (
  <div className="flex gap-2">
    <button
      onClick={() => handleEdit(params.data)}
      className="p-2 hover:bg-default-100 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
      aria-label={`Edit ${params.data.group} group`}
      disabled={isDisabled}
    >
      <Icon icon="mdi:pencil" className="w-4 h-4 text-default-500" />
    </button>
    <ActionButton
      onPress={() => handleDelete(params.data)}
      isLoading={loadingStates.deletingGroups.has(params.data.group)}
      isDisabled={isDisabled}
    />
  </div>
);

// Wrapper components for cell renderers that need props
const createUsersCountCellRenderer = (onUserManagementClick: (groupName: string) => void) => 
  (params: any) => <UsersCountCellRenderer params={params} onUserManagementClick={onUserManagementClick} />;

const createGroupActionCellRenderer = (handleDelete: (data: any) => void, handleEdit: (data: any) => void, loadingStates: { deletingGroups: Set<string>; }, isDisabled?: boolean) => 
  (params: any) => <GroupActionCellRenderer params={params} handleDelete={handleDelete} handleEdit={handleEdit} loadingStates={loadingStates} isDisabled={isDisabled} />;

export const GroupsTab: React.FC = () => {
  const hasAccessManagement = useHasApplicationResource('access_management');
  const [loadingStates] = useState({
    deletingGroups: new Set<string>()
  });
  const [userManagementModal, setUserManagementModal] = useState({
    isOpen: false,
    groupName: ''
  });
  const [groupModal, setGroupModal] = useState({
    isOpen: false,
    mode: 'create' as 'create' | 'edit',
    groupName: ''
  });
  const [deleteGroupModal, setDeleteGroupModal] = useState({
    isOpen: false,
    groupName: ''
  });

  // API hooks
  const { data: fgaGroupsData, isLoading: groupsLoading, refetch: refetchFgaGroups } = useGetFgaGroupsQuery(undefined, { refetchOnMountOrArgChange: true });

  // Handlers
  const handleDelete = useCallback((data: any) => {
    setDeleteGroupModal({ isOpen: true, groupName: data.group });
  }, []);

  const handleEdit = useCallback((data: any) => {
    setGroupModal({ isOpen: true, mode: 'edit', groupName: data.group });
  }, []);

  const handleCreateGroup = useCallback(() => {
    setGroupModal({ isOpen: true, mode: 'create', groupName: '' });
  }, []);

  const handleGroupModalClose = useCallback(() => {
    setGroupModal({ isOpen: false, mode: 'create', groupName: '' });
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setDeleteGroupModal({ isOpen: false, groupName: '' });
    refetchFgaGroups();
  }, [refetchFgaGroups]);

  const handleUserManagementClick = useCallback((groupName: string) => {
    setUserManagementModal({
      isOpen: true,
      groupName
    });
  }, []);

  const handleUserManagementClose = useCallback(() => {
    setUserManagementModal({
      isOpen: false,
      groupName: ''
    });
  }, []);

  if (groupsLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-default-500">Loading groups...</div>
      </div>
    );
  }

  const rowData = fgaGroupsData?.data?.groups?.map((group: any) => ({ 
    group: group.group, 
    application_resources: group.application_resources || [],
    entity_types: group.entity_types || [],
    entity_resources: group.entity_resources || [],
    member_count: group.member_count || 0
  })) || [];
  
  const columnDefs = [
    { 
      headerName: 'Role Name', 
      field: 'group', 
      sortable: true, 
      flex: 1, 
      minWidth: 200, 
      cellRenderer: GroupNameCellRenderer 
    },
    {
      headerName: 'Resource Permissions',
      field: 'resources',
      sortable: false,
      flex: 3,
      minWidth: 400,
      cellRenderer: ResourcePermissionsCellRenderer
    },
    {
      headerName: 'Users',
      field: 'userCount',
      sortable: true,
      flex: 1,
      minWidth: 150,
      cellRenderer: createUsersCountCellRenderer(handleUserManagementClick)
    },
    { 
      headerName: 'Actions', 
      field: 'actions', 
      sortable: false, 
      minWidth: 150, 
      maxWidth: 150, 
      pinned: 'right' as const, 
      lockPinned: true, 
      lockPosition: true, 
      resizable: false, 
      cellRenderer: createGroupActionCellRenderer(handleDelete, handleEdit, loadingStates, !hasAccessManagement)
    }
  ];

  return (
    <>
      <AgGridWrapper rowData={rowData} columnDefs={columnDefs}>
        <div className="mb-4">
          <div className="flex justify-between items-center mb-4">
            <div className="flex flex-col gap-1">
              <h2 className="text-xl font-semibold">Role Management</h2>
              <p className="text-default-500">Create roles and define resource permissions with access levels</p>
            </div>
             <CtyButton 
               size="md" 
               color="primary" 
               variant="solid" 
               onPress={handleCreateGroup}
               startContent={<Icon icon="mdi:plus" width={16} height={16} />}
               isDisabled={!hasAccessManagement}
             >
               Create Role
             </CtyButton>
          </div>
        </div>
      </AgGridWrapper>

      {/* User Management Modal */}
      {userManagementModal.isOpen && (
        <UserManagementModal
          isOpen={userManagementModal.isOpen}
          onOpenChange={handleUserManagementClose}
          context="group"
          groupName={userManagementModal.groupName}
        />
      )}

      {/* Group Modal (Create/Edit) */}
      {groupModal.isOpen && (
        <GroupModal
          isOpen={groupModal.isOpen}
          onOpenChange={handleGroupModalClose}
          mode={groupModal.mode}
          groupName={groupModal.groupName}
        />
      )}

      {/* Delete Group Modal */}
      {deleteGroupModal.isOpen && (
        <DeleteModal
          isOpen={deleteGroupModal.isOpen}
          onOpenChange={handleDeleteModalClose}
          type="group"
          data={{ name: deleteGroupModal.groupName }}
        />
      )}
    </>
  );
};
